import React, { useMemo } from "react";
import { Alert } from "antd";
import { observer } from "mobx-react-lite";
import { t } from "i18next";
import { EntityFiltersTable } from "src/pages/EntityFiltersPage/EntityFiltersTable";
import { CopyOutlined, LoadingOutlined } from "@ant-design/icons";
import { TableStore } from "src/components/tables/TableStore";
import { ZEntityRow } from "src/pages/EntityFiltersPage/EntityList/types";
import { onError } from "src/common/onError";
import { DropdownChildButton } from "src/components/buttons/DropdownChildButton";
import { ZUid } from "src/types/zUid";
import { MultiStepTask } from "src/components/MultiStepTask";
import { MultiStepItem } from "src/components/MultiStepTask/MultiStepItem";
import { nextStep } from "src/components/MultiStepTask/utils";
import { MultiStepEntsTable } from "src/components/MultiStepTask/utils/MultiStepEntsTable";
import { LoaderBox } from "src/components/LoaderBox";
import { ZEntityFiltersChild } from "../../ChildEntitiesStore";
import { CopyInStore } from "./CopyInStore";

interface PropsCopyIn {
  tableStore: TableStore<ZEntityRow, ZEntityFiltersChild>;
  onLoad?: (flag: boolean) => void;
  entityId: ZUid;
  parentObjId: ZUid;
}

export const CopyIn: React.FC<PropsCopyIn> = observer(
  ({ tableStore, entityId, onLoad, parentObjId }) => {
    const store = useMemo(
      () => new CopyInStore(entityId, tableStore, parentObjId),
      [],
    );
    const { pageStore, taskStore } = store;

    const onClick = async () => {
      try {
        onLoad?.(true);
        if (!store.pageStore) await store.init();
        taskStore.open();
      } catch (error) {
        onError(error);
      } finally {
        onLoad?.(false);
      }
    };

    const steps: MultiStepItem[] = [
      {
        title: pageStore?.currObjName || t("Step", { count: 1 }),
        description: t(
          "Select the instance to which you want to copy the previously selected instances of the linked object with all relationships",
        ),
        next: nextStep(!pageStore?.tableStore?.selected.length),
        drawContent: () => (
          <MultiStepEntsTable>
            {pageStore && <EntityFiltersTable store={pageStore} />}
          </MultiStepEntsTable>
        ),
      },
      {
        title: t("Copying"),
        icon: store.isFinalStep ? <LoadingOutlined /> : undefined,
        prev:
          store.result.status === "error"
            ? {
                text: t("Previous step"),
              }
            : undefined,
        onActive() {
          store.save().catch(onError);
        },
        drawContent: () => (
          <LoaderBox
            remoteData={store.result}
            drawReady={() => (
              <Alert
                type="success"
                showIcon
                message={t("Copying was successful")}
              />
            )}
          />
        ),
      },
    ];

    return (
      <>
        <DropdownChildButton
          icon={<CopyOutlined />}
          disabled={tableStore.selected.length === 0}
          onClick={onClick}
        >
          {t("Copy to")}
        </DropdownChildButton>
        <MultiStepTask
          title={t("Copy to")}
          taskStore={store.taskStore}
          steps={steps}
        />
      </>
    );
  },
);
